/**
 * TransferList Utility Functions and Helpers
 *
 * This file contains default implementations for item handlers, filters,
 * and labels used by the TransferList component.
 */

import type { Tooltip } from "@mantine/core";
import type React from "react";
import type {
  TransferListItem,
  TransferListLabels,
  TransferListPagination,
  TransferListPaginationConfig,
  TransferListPaginationSeparateConfig,
  TransferListTooltipConfig,
  TransferListTooltipLabels
} from "./TransferList.types";

type TooltipProps = React.ComponentProps<typeof Tooltip>;

/**
 * Default function to extract a display label from an item.
 * Handles both string items and objects with common label properties.
 *
 * @template T - The type of the item
 * @param item - The item to extract a label from
 * @returns The display label as a string
 *
 * @example
 * defaultGetItemLabel("Simple String") // Returns: "Simple String"
 * defaultGetItemLabel({ label: "User Label" }) // Returns: "User Label"
 * defaultGetItemLabel({ name: "John Doe" }) // Returns: "John Doe"
 * defaultGetItemLabel({ title: "Document Title" }) // Returns: "Document Title"
 */
export const defaultGetItemLabel = <T = TransferListItem>(item: T): string => {
  // Handle string items directly
  if (typeof item === "string") {
    return item;
  }

  // Handle object items by checking common label properties
  if (typeof item === "object" && item !== null) {
    const obj = item as Record<string, unknown>;

    // Try common property names in order of preference
    if (obj.label !== undefined && obj.label !== null) {
      return String(obj.label);
    }
    if (obj.name !== undefined && obj.name !== null) {
      return String(obj.name);
    }
    if (obj.title !== undefined && obj.title !== null) {
      return String(obj.title);
    }

    // Fallback to string representation
    return String(item);
  }

  // Fallback for any other type
  return String(item);
};

/**
 * Default function to extract a unique identifier from an item.
 * Handles both primitive types and objects with common ID properties.
 *
 * @template T - The type of the item
 * @param item - The item to extract a value from
 * @returns The unique identifier as a string or number
 *
 * @example
 * defaultGetItemValue("Simple String") // Returns: "Simple String"
 * defaultGetItemValue(42) // Returns: 42
 * defaultGetItemValue({ id: 123 }) // Returns: 123
 * defaultGetItemValue({ value: "abc" }) // Returns: "abc"
 * defaultGetItemValue({ key: "unique-key" }) // Returns: "unique-key"
 */
export const defaultGetItemValue = <T = TransferListItem>(item: T): string | number => {
  // Handle string items directly
  if (typeof item === "string") {
    return item;
  }

  // Handle number items directly
  if (typeof item === "number") {
    return item;
  }

  // Handle object items by checking common ID properties
  if (typeof item === "object" && item !== null) {
    const obj = item as Record<string, unknown>;

    // Try common property names in order of preference
    if (obj.id !== undefined && obj.id !== null) {
      return typeof obj.id === "number" || typeof obj.id === "string" ? obj.id : String(obj.id);
    }
    if (obj.value !== undefined && obj.value !== null) {
      return typeof obj.value === "number" || typeof obj.value === "string" ? obj.value : String(obj.value);
    }
    if (obj.key !== undefined && obj.key !== null) {
      return String(obj.key);
    }

    // Fallback to JSON stringification for unique identification
    return JSON.stringify(item);
  }

  // Fallback for any other type
  return String(item);
};

/**
 * Default filter function for searching/filtering items.
 * Performs case-insensitive substring matching on the item's label.
 *
 * @template T - The type of the item
 * @param item - The item to filter
 * @param search - The search term to match against
 * @param getLabel - Function to extract the label from the item
 * @returns True if the item matches the search term, false otherwise
 *
 * @example
 * const items = ["Apple", "Banana", "Cherry"];
 * items.filter(item => defaultFilter(item, "app", defaultGetItemLabel))
 * // Returns: ["Apple"]
 *
 * const users = [{ name: "John Doe" }, { name: "Jane Smith" }];
 * users.filter(user => defaultFilter(user, "jane", (u) => u.name))
 * // Returns: [{ name: "Jane Smith" }]
 */
export const defaultFilter = <T = TransferListItem>(item: T, search: string, getLabel: (item: T) => string): boolean => {
  // Empty search matches everything
  if (!search || search.trim() === "") {
    return true;
  }

  // Extract label and normalize for comparison
  const label = getLabel(item).toLowerCase();
  const searchLower = search.toLowerCase().trim();

  // Perform case-insensitive substring match
  return label.includes(searchLower);
};

/**
 * Default labels in Portuguese for the TransferList component.
 * These are used when custom labels are not provided.
 */
export const defaultLabels: Required<TransferListLabels> = {
  searchPlaceholder: "Buscar itens...",
  transferSelected: "Transferir selecionados",
  transferAll: "Transferir todos",
  emptyList: "Lista vazia",
  nothingFound: "Nenhum item encontrado"
};

/**
 * Default pagination configuration with standard values.
 * These are used when pagination is enabled without custom configuration.
 */
export const defaultPaginationConfig: Required<Omit<TransferListPaginationConfig, "onPageChange" | "onPageSizeChange">> & {
  onPageChange: undefined;
  onPageSizeChange: undefined;
} = {
  pageSize: 10,
  pageSizeOptions: [5, 10, 20, 50, 100],
  labels: {
    itemsRange: "{from}-{to} de {total}",
    pageSizeLabel: "Itens por página:"
  },
  classNames: {},
  onPageChange: undefined,
  onPageSizeChange: undefined
};

/**
 * Resolve pagination configuration for a specific list side.
 * Handles different pagination prop formats and merges with defaults.
 *
 * @param pagination - The pagination prop value from InputTransferListProps
 * @param listSide - Which list to resolve configuration for ("left" or "right")
 * @returns Resolved pagination configuration or null if pagination is disabled
 *
 * @example
 * // Pagination disabled
 * resolvePaginationConfig(undefined, "left") // Returns: null
 * resolvePaginationConfig(false, "left") // Returns: null
 *
 * // Pagination enabled with defaults
 * resolvePaginationConfig(true, "left") // Returns: defaultPaginationConfig
 *
 * // Custom configuration for both lists
 * resolvePaginationConfig({ pageSize: 20 }, "left")
 * // Returns: { ...defaultPaginationConfig, pageSize: 20 }
 *
 * // Separate configuration for each list
 * resolvePaginationConfig({ left: { pageSize: 10 }, right: { pageSize: 20 } }, "left")
 * // Returns: { ...defaultPaginationConfig, pageSize: 10 }
 */
export const resolvePaginationConfig = (pagination: TransferListPagination | undefined | false, listSide: "left" | "right"): TransferListPaginationConfig | null => {
  // Pagination is disabled
  if (!pagination) {
    return null;
  }

  // Pagination enabled with default configuration
  if (pagination === true) {
    return { ...defaultPaginationConfig };
  }

  // Check if it's a separate configuration object (has left or right properties)
  const isSeparateConfig = (config: TransferListPaginationConfig | TransferListPaginationSeparateConfig): config is TransferListPaginationSeparateConfig => {
    return "left" in config || "right" in config;
  };

  if (isSeparateConfig(pagination)) {
    // Get configuration for the specific list side
    const sideConfig = pagination[listSide];

    // If no configuration for this side, use defaults
    if (!sideConfig) {
      return { ...defaultPaginationConfig };
    }

    // Merge side-specific configuration with defaults
    return {
      ...defaultPaginationConfig,
      ...sideConfig,
      labels: {
        ...defaultPaginationConfig.labels,
        ...sideConfig.labels
      },
      classNames: {
        ...defaultPaginationConfig.classNames,
        ...sideConfig.classNames
      }
    };
  }

  // Single configuration for both lists - merge with defaults
  return {
    ...defaultPaginationConfig,
    ...pagination,
    labels: {
      ...defaultPaginationConfig.labels,
      ...pagination.labels
    },
    classNames: {
      ...defaultPaginationConfig.classNames,
      ...pagination.classNames
    }
  };
};

/**
 * Default tooltip labels in Portuguese for transfer action buttons.
 * These are used when custom tooltip labels are not provided.
 */
export const defaultTooltipLabels: Required<TransferListTooltipLabels> = {
  sendAll: "Transferir todos os itens",
  sendSelected: "Transferir itens selecionados",
  leftAll: "Transferir todos os itens para a direita",
  leftSelected: "Transferir itens selecionados para a direita",
  rightAll: "Transferir todos os itens para a esquerda",
  rightSelected: "Transferir itens selecionados para a esquerda"
};

/**
 * Default Mantine Tooltip properties for transfer action buttons.
 * These are used when custom tooltip props are not provided.
 */
export const defaultTooltipProps: Partial<TooltipProps> = {
  position: "top",
  withArrow: true,
  transitionProps: { duration: 200 }
};

/**
 * Resolve tooltip label with hierarchical resolution logic.
 * Handles disabled states and falls back through side-specific, generic, and default labels.
 *
 * @param type - Type of action: "all" (transfer all) or "selected" (transfer selected)
 * @param side - Which list side: "left" or "right"
 * @param config - Optional tooltip configuration from props
 * @param disabled - Whether the button is disabled
 * @param disabledReason - Reason for disabled state (shown as tooltip when disabled)
 * @returns Resolved tooltip label string
 *
 * @example
 * // Normal state with defaults
 * resolveTooltipLabel("all", "left")
 * // Returns: "Transferir todos os itens para a direita"
 *
 * // With custom generic label
 * resolveTooltipLabel("selected", "right", { labels: { sendSelected: "Move selected" } })
 * // Returns: "Move selected"
 *
 * // With side-specific label
 * resolveTooltipLabel("all", "left", { labels: { leftAll: "Send all right" } })
 * // Returns: "Send all right"
 *
 * // Disabled state
 * resolveTooltipLabel("all", "left", undefined, true, "Limite de itens atingido")
 * // Returns: "Limite de itens atingido"
 */
export const resolveTooltipLabel = (type: "all" | "selected", side: "left" | "right", config?: TransferListTooltipConfig, disabled?: boolean, disabledReason?: string): string => {
  // Handle disabled states - disabled reason takes highest priority
  if (disabled && disabledReason) {
    return disabledReason;
  }

  // Build key for side-specific label (e.g., "leftAll", "rightSelected")
  const sideSpecificKey = `${side}${type === "all" ? "All" : "Selected"}` as keyof TransferListTooltipLabels;
  // Build key for generic label (e.g., "sendAll", "sendSelected")
  const genericKey = (type === "all" ? "sendAll" : "sendSelected") as keyof TransferListTooltipLabels;

  // Resolution hierarchy: side-specific → generic → default
  return config?.labels?.[sideSpecificKey] || config?.labels?.[genericKey] || defaultTooltipLabels[sideSpecificKey];
};

/**
 * Resolve and merge tooltip props with hierarchical resolution logic.
 * Merges default props with generic props and side-specific props.
 *
 * @param side - Which list side: "left" or "right"
 * @param config - Optional tooltip configuration from props
 * @returns Merged tooltip props for Mantine Tooltip component
 *
 * @example
 * // With defaults only
 * resolveTooltipProps("left")
 * // Returns: { position: "top", withArrow: true, transitionProps: { duration: 200 } }
 *
 * // With generic props
 * resolveTooltipProps("left", { props: { position: "bottom", color: "blue" } })
 * // Returns: { position: "bottom", withArrow: true, transitionProps: { duration: 200 }, color: "blue" }
 *
 * // With side-specific props (overrides generic)
 * resolveTooltipProps("left", {
 *   props: { position: "bottom" },
 *   leftProps: { position: "right", color: "red" }
 * })
 * // Returns: { position: "right", withArrow: true, transitionProps: { duration: 200 }, color: "red" }
 */
export const resolveTooltipProps = (side: "left" | "right", config?: TransferListTooltipConfig): Partial<TooltipProps> => {
  // Get side-specific props based on which list
  const sideProps = side === "left" ? config?.leftProps : config?.rightProps;

  // Merge in order: defaults → generic props → side-specific props
  // Later properties override earlier ones
  return {
    ...defaultTooltipProps,
    ...config?.props,
    ...sideProps
  };
};
